import { useEffect, useMemo } from "react";
import * as React from "react";

import { Loader } from "@livechat/design-system";

import { StyledExternalLink } from "components/external-link";
import { formatPrice } from "helpers/subscription";
import { useTaxDetails } from "routes/subscription/contexts/tax-details-context";

import { redirectToSalesTaxHelpCenter } from "./helpers";
import * as styles from "./styles";
import { useTaxChangePreview } from "./use-tax-change-preview";

export const TaxPreview: React.FC = () => {
  const { country, zipCode } = useTaxDetails();
  const { handleAddressChange, taxChangePreview } = useTaxChangePreview();

  useEffect(() => handleAddressChange({ country, zipCode }), [country, zipCode, handleAddressChange]);

  const { currentTax, isLoading, newTax } = taxChangePreview;

  const taxChangeMessage = useMemo(() => {
    // No tax no message
    if (!currentTax && !newTax) {
      return null;
    }

    // When Tax is added to the subscription
    if (!currentTax && newTax) {
      return `You'll pay an additional ${formatPrice(newTax)} due to the local sales tax. The change will take effect from the next billing period.`;
    }

    // When tax is removed from the subscription
    if (currentTax && !newTax) {
      return `You're no longer subject to the local sales tax. Payment will be reduced by ${formatPrice(
        currentTax
      )} from the next billing period.`;
    }

    // When tax doesn't change
    if (currentTax === newTax) {
      return `You are paying ${formatPrice(newTax)} because of the local sales tax.`;
    }

    // When tax value (region) is changed
    return `When you change location, the sales tax rate changes too. You will be charged ${formatPrice(
      newTax
    )} from the next billing period.`;
  }, [currentTax, newTax]);

  if (isLoading) {
    return <Loader />;
  }

  if (!taxChangeMessage) {
    return null;
  }

  return (
    <div css={styles.taxPreview}>
      <span>{taxChangeMessage}</span>
      <div>
        <StyledExternalLink css={styles.link} onClick={redirectToSalesTaxHelpCenter}>
          Read about sales tax
        </StyledExternalLink>
      </div>
    </div>
  );
};
